import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ArrowRight, BriefcaseBusiness, Flag, Hash, Layers3, Megaphone, Radio,
  RefreshCw, ShieldCheck, UserRound, Users,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatNumber } from '@/lib/utils'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { PageHeader, PageSection } from '@/components/layout/PageHeader'
import type { DashboardMetrics } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import { QueryError } from '@/components/ui/QueryError'

function QueueCard({
  label,
  value,
  description,
  href,
  tone = 'default',
  icon: Icon,
}: {
  label: string
  value: number
  description: string
  href: string
  tone?: 'default' | 'danger' | 'warning'
  icon: typeof Flag
}) {
  return (
    <Link to={href} className={`admin-queue-card is-${tone}`}>
      <span className="admin-queue-icon"><Icon aria-hidden="true" /></span>
      <span className="admin-queue-copy">
        <span>{label}</span>
        <strong>{formatNumber(value)}</strong>
        <small>{description}</small>
      </span>
      <ArrowRight aria-hidden="true" className="admin-queue-arrow" />
    </Link>
  )
}

function ActivityMetric({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="admin-activity-metric">
      <span>{label}</span>
      <strong>{formatNumber(value)}</strong>
      {note && <small>{note}</small>}
    </div>
  )
}

function statusVariant(status: string) {
  if (status === 'operational') return 'success' as const
  if (status === 'degraded' || status === 'unknown' || status === 'n/a') return 'warning' as const
  return 'danger' as const
}

export function DashboardPage() {
  const { permissions } = useAuth()
  const { data, isLoading, error, dataUpdatedAt, refetch, isFetching } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api<DashboardMetrics>('/api/admin/dashboard'),
    refetchInterval: 30_000,
    enabled: !!permissions?.viewReports,
  })

  if (!permissions?.viewReports) {
    return (
      <div className="admin-permission-note">
        <ShieldCheck aria-hidden="true" />
        <div><h1>Staff access is active</h1><p>Your current role does not include platform reporting. Ask an executive or Head of Trust &amp; Safety if this looks wrong.</p></div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader eyebrow="Control room" title="Overview" description="Loading the latest platform state…" />
        <div className="admin-dashboard-skeleton"><span /><span /><span /><span /></div>
      </div>
    )
  }

  if (error || !data) {
    return <QueryError title="Couldn’t load the control room" error={error} onRetry={() => void refetch()} />
  }

  const activeServices = Object.entries(data.infrastructure).filter(([, status]) => status !== 'n/a')
  const healthyServices = activeServices.filter(([, status]) => status === 'operational').length
  const serviceCount = activeServices.length

  return (
    <div className="admin-dashboard">
      <PageHeader
        eyebrow="Control room"
        title="Overview"
        description={`Live queues and platform state · refreshed ${new Date(dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
        actions={
          <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} aria-hidden="true" />
            Refresh
          </Button>
        }
      />

      <PageSection title="Needs attention">
        <div className="admin-queue-grid">
          <QueueCard label="Urgent reports" value={data.trustSafety.urgentReports} description="Highest priority content reviews" href="/trust-safety/reports" tone="danger" icon={Flag} />
          <QueueCard label="Assigned to you" value={data.trustSafety.assignedToMe} description="Investigations waiting on your decision" href="/trust-safety/reports" icon={UserRound} />
          {permissions?.careersAdmin ? (
            <QueueCard label="Applications" value={data.careers.pendingApplications} description="Candidates awaiting review" href="/careers/applications" tone="warning" icon={BriefcaseBusiness} />
          ) : (
            <QueueCard label="Open reports" value={data.trustSafety.openReports} description="Total items in the review queue" href="/trust-safety/reports" tone="warning" icon={ShieldCheck} />
          )}
        </div>
      </PageSection>

      <div className="admin-dashboard-columns">
        <PageSection title="Platform activity" className="admin-dashboard-activity">
          <div className="admin-activity-card">
            <div className="admin-activity-grid">
              <ActivityMetric label="Total people" value={data.users.total} />
              <ActivityMetric label="Active today" value={data.users.dau} />
              <ActivityMetric label="Active this month" value={data.users.mau} />
              <ActivityMetric label="New this week" value={data.users.newRegistrations7d} note={`${formatNumber(data.users.newRegistrations30d)} this month`} />
              <ActivityMetric label="Messages this week" value={data.platform.messages7d} note={`${formatNumber(data.platform.messages30d)} this month`} />
              <ActivityMetric label="Moderation actions" value={data.trustSafety.moderationActions7d} note="last 7 days" />
            </div>
            <div className="admin-directory-links">
              <Link to="/users"><Users aria-hidden="true" /><span><strong>People</strong><small>Search and manage accounts</small></span><ArrowRight aria-hidden="true" /></Link>
              <Link to="/communities"><Radio aria-hidden="true" /><span><strong>{formatNumber(data.platform.communities)} communities</strong><small>Browse community ownership</small></span><ArrowRight aria-hidden="true" /></Link>
              <Link to="/channels"><Hash aria-hidden="true" /><span><strong>{formatNumber(data.platform.channels)} channels</strong><small>Open the channel directory</small></span><ArrowRight aria-hidden="true" /></Link>
            </div>
          </div>
        </PageSection>

        <PageSection title="System status" className="admin-dashboard-status">
          <div className="admin-status-card">
            <div className="admin-status-summary">
              <span><Layers3 aria-hidden="true" /></span>
              <div><strong>{healthyServices}/{serviceCount} services operational</strong><small>Voice: {data.voice.status}</small></div>
              <Link to="/infrastructure">Details</Link>
            </div>
            <div className="admin-status-list">
              {activeServices.map(([name, status]) => (
                <div key={name}>
                  <span>{name.replace(/([A-Z])/g, ' $1')}</span>
                  <Badge variant={statusVariant(status)}>{status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </PageSection>
      </div>

      <PageSection title="Common tasks">
        <div className="admin-quick-actions">
          <Link to="/trust-safety/reports"><Flag aria-hidden="true" /><span><strong>Review a report</strong><small>Open the moderation queue</small></span></Link>
          <Link to="/users"><Users aria-hidden="true" /><span><strong>Find a person</strong><small>Search by username or email</small></span></Link>
          {permissions?.manageAnnouncements && <Link to="/announcements"><Megaphone aria-hidden="true" /><span><strong>Post an announcement</strong><small>Notify clyr users</small></span></Link>}
          {permissions?.careersAdmin && <Link to="/careers/postings"><BriefcaseBusiness aria-hidden="true" /><span><strong>Manage open roles</strong><small>Edit listings and forms</small></span></Link>}
        </div>
      </PageSection>
    </div>
  )
}
